import { useCallback, useEffect, useMemo, useState } from 'react';

import { addDays, format } from 'date-fns';
import { IoIosArrowBack, IoMdDownload } from 'react-icons/io';
import { MdEdit } from 'react-icons/md';
import { useHistory, useParams } from 'react-router-dom';

import { Layout } from '~/components';
import { usePackages } from '~/hooks/packages';
import { useScreen } from '~/hooks/screen';
import { useStudent } from '~/hooks/student';
import api from '~/services/api';
import { cpfFormatter, currencyFormatter } from '~/utils/formatter';

import { BillingProps } from './interface';

import {
  Container,
  Content,
  ContentTitle,
  ContentValue,
  ContentWrapper,
  HeaderButtons,
  PageHeader,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
} from './styles';

interface IParams {
  userId: string;
  billingId: string;
}

const Billing: React.FC = () => {
  const { goBack, push } = useHistory();
  const { userId: studentId, billingId } = useParams<IParams>();
  const { loadPackages, packages } = usePackages();
  const { updateScreen } = useScreen();

  const { studentData, updateStudent } = useStudent();

  const [billing, setBilling] = useState<BillingProps>({} as BillingProps);

  const reportUrl = useMemo(() => {
    return `${process.env.REACT_APP_API_URL}/students/${studentData?.student?.id}/billing/${billing?.id}/pdf`;
  }, [studentData, billing]);

  useEffect(() => {
    async function fetchData() {
      const { data } = await api.get(`billings/${billingId}`);

      setBilling(data);

      console.log(data);
    }

    fetchData();
  }, [billingId, studentId]);

  useEffect(() => {
    loadPackages();
    updateStudent(studentId);
    updateScreen(`billing`);
  }, [loadPackages, updateStudent, studentId, updateScreen]);

  const packageData = packages?.filter(
    (package_) => package_.id === billing.package?.id,
  )[0];

  const payWayVariations: { [key: string]: string } = {
    billet: 'Boleto',
    money: 'Dinheiro',
    cash: 'Dinheiro',
    others: 'Outros',
    debit_card: 'Cartão de débito',
    credit_card: 'Cartão de crédito',
    transfer: 'Transferência',
    '': 'Não informado',
  };

  const statusVariations: { [key: string]: string } = {
    pending: 'Pendente',
    paid: 'Paga',
    overdue: 'Vencida',
    canceled: 'Cancelada',
    '': 'Não informado',
  };

  const loyaltyVariations: { [key: string]: string } = {
    monthly: 'Mensal',
    quarterly: 'Trimestral',
    semiannual: 'Semestral',
    anual: 'Anual',
    others: 'Outros',
    '': 'Não informado',
  };

  const navigateToEditBilling = useCallback(() => {
    const financialStatusOptions = [
      { value: 'pending', label: 'Pendente' },
      { value: 'paid', label: 'Paga' },
      { value: 'overdue', label: 'Vencida' },
      { value: 'canceled', label: 'Cancelada' },
    ];

    const selectedPackage = {
      value: packageData.id,
      label: packageData.name,
      lealtad: packageData.lealtad,
      number_of_training_programs: packageData.number_of_training_programs,
    };

    const defaultBillingStatus = financialStatusOptions.filter(
      (status) => status.value === billing.status,
    )[0];

    const billingData = {
      ...billing,
      package: selectedPackage,
      status: defaultBillingStatus,
    };

    push(
      `/professional/students/${studentId}/billings/${billingId}/edit`,
      billingData,
    );
  }, [push, studentId, billingId, billing, packageData]);

  return (
    <Layout title="Aluno">
      <PageHeader>
        <PageHeaderBackButton onClick={() => goBack()}>
          <IoIosArrowBack />
          <PageHeaderBackButtonLabel>fatura</PageHeaderBackButtonLabel>
        </PageHeaderBackButton>

        <HeaderButtons>
          <button type="button" onClick={navigateToEditBilling}>
            <MdEdit color="#000" size="24" />
          </button>

          <a href={reportUrl} target="_blank" rel="noreferrer">
            <IoMdDownload color="#000" size="24" />
          </a>
        </HeaderButtons>
      </PageHeader>

      <Container>
        <ContentWrapper>
          <Content>
            <ContentTitle>Nome: </ContentTitle>
            <ContentValue>{studentData.name}</ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>CPF: </ContentTitle>
            <ContentValue>{cpfFormatter(studentData.student.cpf)}</ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>ID: </ContentTitle>
            <ContentValue>{billing.id}</ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Pacote: </ContentTitle>
            <ContentValue>{billing.package?.name}</ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Programas: </ContentTitle>
            <ContentValue>
              {billing.package?.number_of_training_programs}
            </ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Fidelização: </ContentTitle>
            <ContentValue>
              {loyaltyVariations[billing.package?.lealtad ?? '']}
            </ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Data de início: </ContentTitle>
            <ContentValue>
              {format(
                addDays(new Date(billing.start_date ?? Date.now()), 1),
                'dd/MM/yyyy',
              )}
            </ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Data de término: </ContentTitle>
            <ContentValue>
              {format(
                addDays(new Date(billing.end_date ?? Date.now()), 1),
                'dd/MM/yyyy',
              )}
            </ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Dia de vencimento: </ContentTitle>
            <ContentValue>{billing.expiration_day}</ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Forma de pagamento: </ContentTitle>
            <ContentValue>
              {payWayVariations[billing.payment_form ?? '']}
            </ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Valor total: </ContentTitle>
            <ContentValue>
              {currencyFormatter(billing.total_amount ?? 0)}
            </ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Valor mensal: </ContentTitle>
            <ContentValue>
              {currencyFormatter(billing.monthly_amount ?? 0)}
            </ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Status: </ContentTitle>
            <ContentValue>
              {statusVariations[billing.status ?? '']}
            </ContentValue>
          </Content>
        </ContentWrapper>

        <ContentWrapper>
          <Content>
            <ContentTitle>Observações: </ContentTitle>
            <ContentValue>{billing?.observations}</ContentValue>
          </Content>
        </ContentWrapper>
      </Container>
    </Layout>
  );
};

export default Billing;
